import React, { useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import { useBlocker } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  Edit,
  ImageOutlined,
  Save,
  Close,
  Settings,
  Add,
  Tune,
} from "@mui/icons-material";
import { QUANTIDADE_DESTINOS_PING } from "../../../../../utils/pingMacros";
import api from "../../../../../services/api";
import DatagridComponent from "../../../../../components/datagridComponent";
import useStyles from "./styles";
import dayjs from "dayjs";
import InternoModalConfirmacaoSaida from "../../../../../components/modalSaindoDaPagina";
import ModalPingIcon from "./modalPingIcon";

// Registro de GeneralSettings onde ficam os 5 destinos padrão (slots A–E)
const PING_PRESETS_PRODUCT = "pingPresets";

export default function PingIcons() {
  const theme = useTheme();
  const styles = useStyles(theme);

  // --- Estados do Componente ---
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Estados do Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [modalData, setModalData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Destinos padrão (slots A–E) salvos em GeneralSettings: array de 5 ids|null
  const [pingDefaults, setPingDefaults] = useState(
    Array(QUANTIDADE_DESTINOS_PING).fill(null),
  );
  const [defaultsModalOpen, setDefaultsModalOpen] = useState(false);
  const [defaultsDraft, setDefaultsDraft] = useState(
    Array(QUANTIDADE_DESTINOS_PING).fill(null),
  );
  const [savingDefaults, setSavingDefaults] = useState(false);

  // --- Lógica de Bloqueio de Saída ---

  const shouldBlock = hasUnsavedChanges;

  // Intercepta fechamento da aba ou recarregamento da página
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.returnValue = "Existem alterações não salvas. Deseja realmente sair?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldBlock]);

  // Intercepta navegação interna do React Router
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      shouldBlock && currentLocation.pathname !== nextLocation.pathname,
  );

  // --- Ciclo de Vida e Busca de Dados ---
  useEffect(() => {
    fetchIcons();
    fetchDefaults();
    return () => {
      // Limpa as URLs de objetos criadas para preview ao desmontar
      icons.forEach((icon) => {
        if (icon.imageUrl) URL.revokeObjectURL(icon.imageUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Busca os destinos padrão (slots A–E) salvos em GeneralSettings
  const fetchDefaults = async () => {
    try {
      const response = await api.post("/interno/find/generalSetting", {
        product: PING_PRESETS_PRODUCT,
      });
      const defaults = Array.isArray(response?.data?.defaults)
        ? response.data.defaults
        : [];
      const normalized = Array(QUANTIDADE_DESTINOS_PING)
        .fill(null)
        .map((_, i) => defaults[i] ?? null);
      setPingDefaults(normalized);
    } catch (error) {
      console.error("Erro ao buscar destinos padrão de ping:", error);
    }
  };

  // Busca a lista de ícones cadastrados
  const fetchIcons = async () => {
    setLoading(true);
    try {
      const response = await api.post("/anexos/findMany", {
        type: "ping_icon",
      });
      const data = response?.data || [];

      // Baixa cada imagem individualmente para gerar a URL de preview
      const iconsWithPreview = await Promise.all(
        data.map(async (icon) => {
          try {
            const imgRes = await api.post(
              "/anexos/download",
              { id: icon.id },
              { responseType: "arraybuffer" },
            );
            const blob = new Blob([imgRes.data], { type: icon.mimeType });
            return { ...icon, imageUrl: URL.createObjectURL(blob) };
          } catch (e) {
            console.error("Erro ao carregar preview do ícone", icon.id, e);
            return { ...icon, imageUrl: null };
          }
        }),
      );

      setIcons(iconsWithPreview);
    } catch (error) {
      console.error("Erro ao buscar ícones:", error);
      setSnackbar({
        children: "Erro ao buscar ícones de ping!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // --- Funções Auxiliares ---
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // --- Manipuladores de Ações (CRUD) ---
  const handleSaveModal = async ({
    description,
    destino,
    file,
    isNewImage,
  }) => {
    try {
      const metadata = { destino: destino || "" };
      if (modalMode === "create") {
        const base64 = await convertToBase64(file);
        await api.post("/anexos/create", {
          nome: file.name,
          mimeType: file.type,
          dadosBase64: base64,
          type: "ping_icon",
          descricao: description,
          metadata,
        });
        setSnackbar({
          children: "Ícone cadastrado com sucesso!",
          severity: "success",
        });
      } else {
        // Update
        if (isNewImage && file) {
          const base64 = await convertToBase64(file);
          // O backend já está preparado para receber os dados do arquivo no update
          await api.post("/anexos/update", {
            id: modalData.id,
            descricao: description,
            metadata,
            nome: file.name,
            mimeType: file.type,
            dadosBase64: base64,
          });
        } else {
          // Apenas atualiza a descrição e os metadados (destino/origem)
          await api.post("/anexos/update", {
            id: modalData.id,
            descricao: description,
            metadata,
          });
        }
        setSnackbar({
          children: "Ícone atualizado com sucesso!",
          severity: "success",
        });
      }
      setIsModalOpen(false);
      setHasUnsavedChanges(false);
      fetchIcons();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      setSnackbar({
        children: "Erro ao salvar ícone.",
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.post("/anexos/delete", { id: deleteConfirm.id });
      setSnackbar({
        children: "Ícone excluído com sucesso!",
        severity: "success",
      });
      if (deleteConfirm.imageUrl) URL.revokeObjectURL(deleteConfirm.imageUrl);
      fetchIcons();
    } catch (error) {
      console.error("Erro ao excluir:", error);
      setSnackbar({
        children: "Erro ao excluir ícone.",
        severity: "error",
      });
    } finally {
      setDeleteConfirm(null);
    }
  };

  // --- Destinos Padrão (slots A–E) ---
  const handleOpenDefaults = () => {
    // Resolve cada id salvo para o preset atual; padrão excluído vira slot vazio
    const draft = pingDefaults.map(
      (id) => icons.find((ic) => ic.id === id) || null,
    );
    setDefaultsDraft(draft);
    setDefaultsModalOpen(true);
  };

  const handleSaveDefaults = async () => {
    setSavingDefaults(true);
    try {
      const defaults = defaultsDraft.map((p) => p?.id || null);
      await api.post("/interno/upsert/generalSettings", {
        product: PING_PRESETS_PRODUCT,
        settings: { defaults },
      });
      setPingDefaults(
        Array(QUANTIDADE_DESTINOS_PING)
          .fill(null)
          .map((_, i) => defaults[i] ?? null),
      );
      setSnackbar({
        children: "Destinos padrão salvos com sucesso!",
        severity: "success",
      });
      setDefaultsModalOpen(false);
    } catch (error) {
      console.error("Erro ao salvar destinos padrão:", error);
      setSnackbar({
        children: "Erro ao salvar destinos padrão.",
        severity: "error",
      });
    } finally {
      setSavingDefaults(false);
    }
  };

  // --- Controle do Menu de Ações ---
  const openMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  // --- Configuração das Colunas do DataGrid ---
  const columns = [
    {
      field: "preview",
      headerName: "Preview",
      width: 100,
      renderCell: (params) => (
        <Avatar
          variant="rounded"
          src={params.row.imageUrl}
          sx={styles.avatarPreview}
          onClick={() => setPreviewImage(params.row.imageUrl)}
        >
          <ImageOutlined />
        </Avatar>
      ),
    },
    { field: "nome", headerName: "Nome do Arquivo", flex: 1 },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 1,
      renderCell: (params) => {
        return params.value || "Sem descrição";
      },
    },
    {
      field: "destino",
      headerName: "Destino (IP/Link)",
      flex: 1,
      valueGetter: (value, row) => row.metadata?.destino || "",
      renderCell: (params) => params.value || "—",
    },
    {
      field: "padrao",
      headerName: "Padrão",
      width: 90,
      sortable: false,
      renderCell: (params) => {
        const i = pingDefaults.indexOf(params.row.id);
        return i >= 0 ? String.fromCharCode(65 + i) : "—";
      },
    },
    {
      field: "createdAt",
      headerName: "Data de Cadastro",
      width: 180,
      valueFormatter: (value) => dayjs(value).format("DD/MM/YYYY HH:mm"),
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 150,
      renderCell: (params) => {
        return (
          <Box sx={styles.actionBox}>
            <IconButton
              color="primary"
              size="small"
              onClick={(e) => openMenu(e, params.row)}
            >
              <Settings />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  // --- Renderização do Componente ---
  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Paper sx={styles.mainPaper}>
        <Box sx={styles.containerHeader}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ImageOutlined color="primary" />
            <Typography variant="h5" sx={styles.title}>
              Lista de Pings
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Tune />}
              sx={styles.headerButton}
              onClick={handleOpenDefaults}
            >
              Configurar Padrões
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              sx={styles.headerButton}
              onClick={() => {
                setModalMode("create");
                setModalData(null);
                setIsModalOpen(true);
              }}
            >
              Adicionar Novo
            </Button>
          </Box>
        </Box>
        <Paper variant="outlined" sx={styles.paperBody}>
          {/* Bloco da Listagem (DataGrid) */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper variant="outlined" elevation={3} sx={styles.paperDatagrid}>
              <DatagridComponent
                columns={columns}
                rows={icons.map((item, index) => ({ ...item, id: item.id }))}
                options={{
                  slots: {
                    noRowsOverlay: () => (
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                          justifyContent: "center",
                          alignItems: "center",
                          pt: "10px",
                        }}
                      >
                        Nenhum ícone cadastrado. Use os campos acima para
                        cadastrar ícones que serão exibidos no dashboard de
                        acordo com a descrição do destino.
                      </Box>
                    ),
                  },
                }}
                sxDatagrid={styles.datagridSx}
              />
            </Paper>
          )}
        </Paper>
      </Paper>

      {/* Menu de Contexto das Linhas */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            setModalMode("update");
            setModalData(menuRow);
            setIsModalOpen(true);
            closeMenu();
          }}
        >
          <Edit sx={{ mr: 1 }} fontSize="small" color="primary" /> Editar
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteConfirm(menuRow);
            closeMenu();
          }}
        >
          <Delete sx={{ mr: 1 }} fontSize="small" color="error" /> Excluir
        </MenuItem>
      </Menu>

      {/* Modais de Confirmação e Alertas */}
      <InternoModalConfirmacaoSaida
        openConfirmacao={blocker.state === "blocked"}
        setOpenConfirmacao={(open) => {
          if (!open && blocker.state === "blocked") blocker.reset();
        }}
        title="Atenção"
        headerText="Existem alterações não salvas."
        contentText="Deseja realmente sair desta página? As alterações não salvas serão perdidas."
        confirmText="Sair"
        execute={() => blocker.proceed()}
        cancel={() => blocker.reset()}
        severity="warning"
      />

      <InternoModalConfirmacaoSaida
        openConfirmacao={Boolean(deleteConfirm)}
        setOpenConfirmacao={(open) => {
          if (!open) setDeleteConfirm(null);
        }}
        title="Excluir Ícone?"
        headerText={`Tem certeza que deseja excluir o ícone do destino "${deleteConfirm?.descricao}"?`}
        contentText="Essa ação não pode ser desfeita e o dashboard passará a usar o ícone padrão se não houver substituto."
        confirmText="Excluir"
        execute={handleDelete}
        cancel={() => setDeleteConfirm(null)}
        severity="error"
      />

      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
      >
        <Box sx={styles.previewDialogBox}>
          <img
            src={previewImage}
            alt="Preview"
            style={styles.previewDialogImg}
          />
        </Box>
      </Dialog>

      {/* Modal de configuração dos destinos padrão (slots A–E) */}
      <Dialog
        open={defaultsModalOpen}
        onClose={() => setDefaultsModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Configurar Destinos Padrão
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Defina os destinos que vêm pré-selecionados ao habilitar o ping na
            criação/edição de hosts.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {defaultsDraft.map((selected, index) => (
              <Autocomplete
                key={index}
                options={icons}
                value={selected}
                onChange={(e, newValue) => {
                  setDefaultsDraft((prev) => {
                    const next = [...prev];
                    next[index] = newValue;
                    return next;
                  });
                }}
                getOptionLabel={(o) => o?.descricao || "Sem descrição"}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Avatar
                      variant="rounded"
                      src={option.imageUrl}
                      sx={{ width: 24, height: 24 }}
                    >
                      <ImageOutlined fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">
                        {option.descricao || "Sem descrição"}
                      </Typography>
                      {option.metadata?.destino && (
                        <Typography variant="caption" color="text.secondary">
                          {option.metadata.destino}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Destino ${String.fromCharCode(65 + index)}`}
                    size="small"
                  />
                )}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setDefaultsModalOpen(false)}
            disabled={savingDefaults}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDefaults}
            disabled={savingDefaults}
          >
            {savingDefaults ? <CircularProgress size={24} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ModalPingIcon
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveModal}
        mode={modalMode}
        initialData={modalData}
        setHasUnsavedChanges={setHasUnsavedChanges}
      />

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
}
